// @ts-strict-ignore
import React from 'react';
import styled from 'styled-components';

import { ThreeSectionSliderBlockData } from 'api/cms';
import { Box } from 'components/Box';
import { Button, DotNavigationButton } from 'components/Button';
import { Flex } from 'components/Flex';
import { Icon } from 'components/Icon';
import { RichTextHeadline } from 'components/Text';
import Carousel, { Slide } from 'containers/Carousel';
import { breakpoints, colors, transition } from 'theme';
import { range } from 'utils/functional';

import { ThreeSectionSliderBlockSection } from './ThreeSectionSliderBlockSection';

const ArrowNavigation = styled(
  ({ className, nextPage, prevPage, current, total, ...props }) => {
    const title = {
      prev: getText('Previous page'),
      next: getText('Next page'),
    };
    const prevDisabled = current <= 0;
    const nextDisabled = current >= total - 1;
    return (
      <Flex
        justifyContent="space-between"
        alignItems="center"
        className={className}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        <Button
          onClick={prevPage}
          aria-label={title.prev}
          title={title.prev}
          disabled={prevDisabled}
          data-qa-id="carousel-left-button"
        >
          <Icon
            initialColorName="todo-replace-me"
            name="chevron-left"
            size={24}
          />
        </Button>
        <Button
          onClick={nextPage}
          aria-label={title.next}
          title={title.next}
          disabled={nextDisabled}
          data-qa-id="carousel-right-button"
        >
          <Icon
            initialColorName="todo-replace-me"
            name="chevron-right"
            size={24}
          />
        </Button>
      </Flex>
    );
  }
)`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  width: calc(100% + 128px);
  padding: 0 12px;
  max-width: calc(100vw - 16px);
  button {
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    color: ${colors.waterBlue};
    transition: all ${transition};
    svg {
      * {
        fill: currentColor;
      }
    }
    &[disabled] {
      opacity: 0.33;
    }
    @media (min-width: ${breakpoints[2]}) {
      width: 40px;
      height: 40px;
      padding: 0;
      color: white;
      background: ${colors.waterBlue};
      &[disabled] {
        opacity: 0.33;
        &:hover {
          background: ${colors.waterBlue};
        }
      }
      &:hover,
      &:focus {
        background: ${colors.turtleGreen};
      }
    }
  }
`;

const CarouselContainer = styled(Box)`
  position: relative;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    z-index: 1;
    height: 100%;
    width: 2px;
    background: white;
  }
`;

type Props = {
  data: ThreeSectionSliderBlockData;
};

export function ThreeSectionSliderBlock({
  data: { headline, imageSize, sections },
}: Props) {
  const withImages = sections.some((section) => section.image);

  return (
    <>
      {headline && (
        <RichTextHeadline textAlign="center">{headline}</RichTextHeadline>
      )}
      <CarouselContainer mt="40px">
        <Carousel
          data-qa-id="three-section-slider-carousel"
          pageLength={[1, 1, 2, 3]}
          navigation={[
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line react/no-unstable-nested-components
            ({ current, total, goToPage, prevPage, nextPage }) => (
              <nav>
                {total > 1 && (
                  <>
                    <ArrowNavigation
                      nextPage={nextPage}
                      prevPage={prevPage}
                      current={current}
                      total={total}
                    />
                    <Flex mt="20px" justifyContent="center">
                      {range(total).map((index) => (
                        <DotNavigationButton
                          key={index}
                          goToPage={goToPage}
                          index={index}
                          isActive={index === current}
                        />
                      ))}
                    </Flex>
                  </>
                )}
              </nav>
            ),
          ]}
          slides={() =>
            sections.map((section, index) => (
              <Slide // eslint-disable-next-line react/no-array-index-key
                key={`${'section'}-${index}`}
                // @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: Element; key: string; height: st... Remove this comment to see the full error message
                height="100%"
              >
                <ThreeSectionSliderBlockSection
                  imageQaId="slider-image"
                  text={section.content}
                  image={section.image}
                  imageSize={imageSize}
                  withImages={withImages}
                  borderLeft={[
                    null,
                    null,
                    index > 0 ? `1px solid ${colors.silver}` : undefined,
                  ]}
                />
              </Slide>
            ))
          }
        />
      </CarouselContainer>
    </>
  );
}
